import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useState } from 'react';
import { platformApi, TenantDetail, COMP_TYPE_OPTIONS, compTypeLabel, type ImChannel } from '@/api/platform';
import { formatImSyncAppliedMessage } from '@/utils/dingtalk-sync-message';

const STATUS_MAP: Record<string, { color: string; text: string }> = {
  ACTIVE: { color: 'green', text: '正常' },
  SUSPENDED: { color: 'orange', text: '已停用' },
  ARCHIVED: { color: 'default', text: '已归档' },
};

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantId = Number(id);

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => platformApi.getTenant(tenantId),
    enabled: !!tenantId,
  });

  if (isLoading) return <Spin className="flex justify-center mt-20" />;
  if (!tenant) return <div>租户不存在</div>;

  return (
    <div className="space-y-4">
      <Button icon={<ArrowLeftOutlined />} type="link" onClick={() => navigate('/tenants')}>
        返回列表
      </Button>

      <BasicInfoCard tenant={tenant} tenantId={tenantId} queryClient={queryClient} />
      <TokenManageCard tenant={tenant} tenantId={tenantId} queryClient={queryClient} />
      <AdminConfigCard tenant={tenant} tenantId={tenantId} queryClient={queryClient} />
      {tenant.imChannel === 'wecom' ? (
        <WecomConfigCard tenant={tenant} tenantId={tenantId} queryClient={queryClient} />
      ) : (
        <DingtalkConfigCard tenant={tenant} tenantId={tenantId} queryClient={queryClient} />
      )}
      <ImSyncCard
        tenantId={tenantId}
        tenantName={tenant.name}
        imChannel={tenant.imChannel}
      />
    </div>
  );
}

function BasicInfoCard({ tenant, tenantId, queryClient }: { tenant: TenantDetail; tenantId: number; queryClient: any }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>(tenant.status);

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; logo?: string; maxEmployees?: number; compType?: string; imChannel?: ImChannel }) =>
      platformApi.updateTenant(tenantId, data),
    onSuccess: () => {
      message.success('更新成功');
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) => platformApi.updateTenantStatus(tenantId, status),
    onSuccess: () => {
      message.success('状态更新成功');
      setStatusModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const statusInfo = STATUS_MAP[tenant.status] || { color: 'default', text: tenant.status };

  return (
    <>
      <Card
        title="基本信息"
        extra={
          <Space>
            <Button onClick={() => { setStatusModalOpen(true); setNewStatus(tenant.status); }}>
              切换状态
            </Button>
            <Button
              type="primary"
              onClick={() => {
                editForm.setFieldsValue({
                  name: tenant.name,
                  logo: tenant.logo,
                  maxEmployees: tenant.maxEmployees,
                  compType: tenant.compType ?? 'GENERAL',
                  imChannel: tenant.imChannel,
                });
                setEditOpen(true);
              }}
            >
              编辑
            </Button>
          </Space>
        }
      >
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="ID">{tenant.id}</Descriptions.Item>
          <Descriptions.Item label="Slug">
            <code className="bg-gray-50 px-1.5 py-0.5 rounded text-xs">{tenant.slug}</code>
          </Descriptions.Item>
          <Descriptions.Item label="公司名称">{tenant.name}</Descriptions.Item>
          <Descriptions.Item label="行业类型">{compTypeLabel(tenant.compType)}</Descriptions.Item>
          <Descriptions.Item label="IM 渠道">
            <Tag color={tenant.imChannel === 'wecom' ? 'green' : 'blue'}>
              {tenant.imChannel === 'wecom' ? '企业微信' : '钉钉'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="员工数">{tenant.employeeCount}</Descriptions.Item>
          <Descriptions.Item label="员工上限">{tenant.maxEmployees ?? '不限'}</Descriptions.Item>
          <Descriptions.Item label="盘点批次">{tenant.batchCount} 个</Descriptions.Item>
          <Descriptions.Item label="Token 余额">{Number(tenant.tokenBalance).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{dayjs(tenant.createdAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{dayjs(tenant.updatedAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        title="编辑租户信息"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => editForm.submit()}
        confirmLoading={updateMutation.isPending}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={(v) => updateMutation.mutate(v)}>
          <Form.Item name="name" label="公司名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="logo" label="Logo URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="maxEmployees" label="员工上限">
            <InputNumber min={1} className="w-full" placeholder="留空=不限" />
          </Form.Item>
          <Form.Item name="compType" label="行业类型">
            <Select options={COMP_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="imChannel" label="IM 渠道">
            <Select
              options={[
                { value: 'dingtalk', label: '钉钉' },
                { value: 'wecom', label: '企业微信' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="切换租户状态"
        open={statusModalOpen}
        onCancel={() => setStatusModalOpen(false)}
        onOk={() => statusMutation.mutate(newStatus)}
        confirmLoading={statusMutation.isPending}
      >
        <div className="py-4">
          <Typography.Text>当前状态：</Typography.Text>
          <Tag color={statusInfo.color} className="ml-2">{statusInfo.text}</Tag>
          <Divider />
          <Typography.Text>切换为：</Typography.Text>
          <Select
            value={newStatus}
            onChange={setNewStatus}
            className="ml-2 w-32"
            options={[
              { value: 'ACTIVE', label: '正常' },
              { value: 'SUSPENDED', label: '停用' },
              { value: 'ARCHIVED', label: '归档' },
            ]}
          />
          {newStatus !== 'ACTIVE' && (
            <Typography.Text type="warning" className="block mt-3">
              停用或归档后，该租户所有员工将无法登录
            </Typography.Text>
          )}
        </div>
      </Modal>
    </>
  );
}

function TokenManageCard({ tenant, tenantId, queryClient }: { tenant: TenantDetail; tenantId: number; queryClient: any }) {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: (values: { amount: number; reason?: string }) =>
      platformApi.adjustToken(tenantId, values.amount, values.reason),
    onSuccess: (data: any) => {
      const adjusted = data?.adjusted ?? 0;
      message.success(`Token 余额已${adjusted > 0 ? '充值' : '扣减'} ${Math.abs(adjusted).toLocaleString()}`);
      setAdjustOpen(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  return (
    <>
      <Card
        title="Token 管理"
        extra={
          <Button type="primary" onClick={() => setAdjustOpen(true)}>
            调整余额
          </Button>
        }
      >
        <div className="flex items-center gap-8">
          <div>
            <Typography.Text type="secondary">当前余额</Typography.Text>
            <div className="text-3xl font-bold text-blue-600 mt-1">
              {Number(tenant.tokenBalance).toLocaleString()}
            </div>
          </div>
        </div>
      </Card>

      <Modal
        title="调整 Token 余额"
        open={adjustOpen}
        onCancel={() => setAdjustOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={mutation.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(v) => mutation.mutate(v)}>
          <Form.Item
            name="amount"
            label="调整数量"
            rules={[{ required: true, message: '请输入调整数量' }]}
            extra="正数=充值，负数=扣减"
          >
            <InputNumber className="w-full" placeholder="如 10000 或 -5000" />
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <Input placeholder="如：平台手动充值" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function AdminConfigCard({ tenant, tenantId, queryClient }: { tenant: TenantDetail; tenantId: number; queryClient: any }) {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: { adminEmpNos?: string; superAdminEmpNos?: string }) =>
      platformApi.updateTenantAdmins(tenantId, data),
    onSuccess: () => {
      message.success('管理员配置已更新');
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });

  return (
    <Card
      title="管理员配置"
      extra={
        editing ? (
          <Space>
            <Button onClick={() => setEditing(false)}>取消</Button>
            <Button type="primary" onClick={() => form.submit()} loading={mutation.isPending}>保存</Button>
          </Space>
        ) : (
          <Button onClick={() => {
            form.setFieldsValue({
              adminEmpNos: tenant.adminEmpNos,
              superAdminEmpNos: tenant.superAdminEmpNos,
            });
            setEditing(true);
          }}>编辑</Button>
        )
      }
    >
      {editing ? (
        <Form form={form} layout="vertical" onFinish={(v) => mutation.mutate(v)}>
          <Form.Item name="adminEmpNos" label="管理员工号（逗号分隔）">
            <Input placeholder="EMP001,EMP002" />
          </Form.Item>
          <Form.Item name="superAdminEmpNos" label="超级管理员工号（逗号分隔）">
            <Input placeholder="EMP001" />
          </Form.Item>
        </Form>
      ) : (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="管理员工号">{tenant.adminEmpNos || '-'}</Descriptions.Item>
          <Descriptions.Item label="超级管理员工号">{tenant.superAdminEmpNos || '-'}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

function DingtalkConfigCard({ tenant, tenantId, queryClient }: { tenant: TenantDetail; tenantId: number; queryClient: any }) {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: Record<string, string | undefined>) =>
      platformApi.updateTenantDingtalk(tenantId, data),
    onSuccess: () => {
      message.success('钉钉配置已更新');
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });

  return (
    <Card
      title="钉钉配置"
      extra={
        <>
          {tenant.dingtalkAppKey && tenant.dingtalkAgentId ? (
            <Tag color="green" className="mr-2">已配置</Tag>
          ) : (
            <Tag color="orange" className="mr-2">未配置</Tag>
          )}
          {tenant.slug === 'demo' ? (
            <Tag className="mr-2">demo 可 fallback .env</Tag>
          ) : null}
          {editing ? (
            <Space>
              <Button onClick={() => setEditing(false)}>取消</Button>
              <Button type="primary" onClick={() => form.submit()} loading={mutation.isPending}>保存</Button>
            </Space>
          ) : (
            <Button onClick={() => {
              form.setFieldsValue({
                dingtalkCorpId: tenant.dingtalkCorpId,
                dingtalkAppKey: tenant.dingtalkAppKey,
                dingtalkAppSecret: '',
                dingtalkAgentId: tenant.dingtalkAgentId,
                dingtalkRobotCode: tenant.dingtalkRobotCode,
              });
              setEditing(true);
            }}>编辑</Button>
          )}
        </>
      }
    >
      {editing ? (
        <Form form={form} layout="vertical" onFinish={(v) => mutation.mutate(v)}>
          <Form.Item name="dingtalkCorpId" label="Corp ID">
            <Input placeholder="dingXXXXXX" />
          </Form.Item>
          <Form.Item name="dingtalkAppKey" label="App Key">
            <Input placeholder="dingXXXXXX" />
          </Form.Item>
          <Form.Item name="dingtalkAppSecret" label="App Secret">
            <Input.Password placeholder="留空=不修改" />
          </Form.Item>
          <Form.Item name="dingtalkAgentId" label="Agent ID">
            <Input />
          </Form.Item>
          <Form.Item name="dingtalkRobotCode" label="Robot Code">
            <Input />
          </Form.Item>
        </Form>
      ) : (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Corp ID">{tenant.dingtalkCorpId || '-'}</Descriptions.Item>
          <Descriptions.Item label="App Key">{tenant.dingtalkAppKey || '-'}</Descriptions.Item>
          <Descriptions.Item label="App Secret">{tenant.dingtalkAppSecret ? '******' : '-'}</Descriptions.Item>
          <Descriptions.Item label="Agent ID">{tenant.dingtalkAgentId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Robot Code">{tenant.dingtalkRobotCode || '-'}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

function WecomConfigCard({ tenant, tenantId, queryClient }: { tenant: TenantDetail; tenantId: number; queryClient: any }) {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);

  const testConnection = async () => {
    setHealthLoading(true);
    try {
      const r = await platformApi.testTenantWecom(tenantId);
      if (r.ok) {
        message.success('企微连接成功');
      } else {
        message.error(r.message || '连接失败');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '连接失败');
    } finally {
      setHealthLoading(false);
    }
  };

  const mutation = useMutation({
    mutationFn: (data: Record<string, string | undefined>) =>
      platformApi.updateTenantWecom(tenantId, data),
    onSuccess: () => {
      message.success('企微配置已更新');
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });

  return (
    <Card
      title="企微配置"
      extra={
        <Space>
          {tenant.wecomCorpId && tenant.wecomAgentId ? (
            <Tag color="green">已配置</Tag>
          ) : (
            <Tag color="orange">未配置</Tag>
          )}
          <Button onClick={testConnection} loading={healthLoading}>
            测试连接
          </Button>
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)}>取消</Button>
              <Button type="primary" onClick={() => form.submit()} loading={mutation.isPending}>保存</Button>
            </>
          ) : (
            <Button onClick={() => {
              form.setFieldsValue({
                wecomCorpId: tenant.wecomCorpId,
                wecomAgentId: tenant.wecomAgentId,
                wecomSecret: '',
              });
              setEditing(true);
            }}>编辑</Button>
          )}
        </Space>
      }
    >
      {editing ? (
        <Form form={form} layout="vertical" onFinish={(v) => mutation.mutate(v)}>
          <Form.Item name="wecomCorpId" label="Corp ID">
            <Input placeholder="wwXXXXXX" />
          </Form.Item>
          <Form.Item name="wecomAgentId" label="Agent ID">
            <Input />
          </Form.Item>
          <Form.Item name="wecomSecret" label="Secret">
            <Input.Password placeholder="留空=不修改" />
          </Form.Item>
        </Form>
      ) : (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Corp ID">{tenant.wecomCorpId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Agent ID">{tenant.wecomAgentId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Secret">{tenant.wecomSecret ? '******' : '-'}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

function ImSyncCard({
  tenantId,
  tenantName,
  imChannel,
}: {
  tenantId: number;
  tenantName: string;
  imChannel: ImChannel;
}) {
  const [healthLoading, setHealthLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const imLabel = imChannel === 'wecom' ? '企微' : '钉钉';

  const testConnection = async () => {
    setHealthLoading(true);
    try {
      const r =
        imChannel === 'wecom'
          ? await platformApi.testTenantWecom(tenantId)
          : await platformApi.checkDingtalkHealth(tenantId);
      if (r.ok) {
        message.success(`${imLabel}连接正常，access_token 获取成功`);
      } else {
        message.error(r.message || '连接失败');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '连接失败');
    } finally {
      setHealthLoading(false);
    }
  };

  const runFullSync = async () => {
    setSyncLoading(true);
    try {
      const dry = await platformApi.syncTenantIm(tenantId, { apply: false });
      const total = dry.added.length + dry.changed.length + dry.removed.length;
      if (total === 0) {
        Modal.confirm({
          title: `刷新组织架构 · ${tenantName}`,
          width: 520,
          okText: '刷新组织架构',
          cancelText: '取消',
          content: (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              员工花名册与{imLabel}一致，无新增/变更/离职。是否仍同步<strong>部门树</strong>并更新员工
              <strong>部门路径</strong>？（租户报表中心依赖此数据）
            </Typography.Paragraph>
          ),
          onOk: async () => {
            const applied = await platformApi.syncTenantIm(tenantId, { apply: true });
            message.success(formatImSyncAppliedMessage(imLabel, applied.applied));
          },
        });
        return;
      }
      Modal.confirm({
        title: `从${imLabel}全量同步 · ${tenantName}`,
        width: 640,
        okText: `确认入库（共 ${total} 条变更）`,
        cancelText: '只看不动',
        content: (
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <Typography.Paragraph type="secondary">
              平台调试入口，逻辑与租户 PC 花名册「从{imLabel}同步」相同。新增员工的人才池/序列由职位推断，入库后可在花名册修正。
            </Typography.Paragraph>
            {dry.removed.length > 0 ? (
              <Typography.Paragraph type="warning">
                将把 {dry.removed.length} 名员工标记为「离职」：{' '}
                {dry.removed.map((x) => x.name).join('、')}
              </Typography.Paragraph>
            ) : null}
            {dry.added.length > 0 ? (
              <Typography.Paragraph>
                新增 {dry.added.length} 人：{dry.added.map((x) => x.name).slice(0, 20).join('、')}
                {dry.added.length > 20 ? '…' : ''}
              </Typography.Paragraph>
            ) : null}
            {dry.changed.length > 0 ? (
              <Typography.Paragraph>变更 {dry.changed.length} 人</Typography.Paragraph>
            ) : null}
          </div>
        ),
        onOk: async () => {
          const applied = await platformApi.syncTenantIm(tenantId, { apply: true });
          message.success(formatImSyncAppliedMessage(imLabel, applied.applied));
        },
      });
    } catch (e: any) {
      Modal.error({
        title: `${imLabel}同步失败`,
        content: e?.response?.data?.message || e?.message || '未知错误',
      });
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <Card
      title={`${imLabel}花名册同步（平台调试）`}
      extra={
        <Space>
          <Button onClick={testConnection} loading={healthLoading}>
            测试连接
          </Button>
          <Button type="primary" onClick={runFullSync} loading={syncLoading}>
            从{imLabel}全量同步
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        与租户 PC 花名册「从{imLabel}同步」同一套 API。全量同步会写入部门树 + 员工部门路径；即使员工无变更，也会提示「刷新组织架构」。请先保存上方{imLabel}凭据再测试。
      </Typography.Paragraph>
    </Card>
  );
}
